import { z } from "zod";
import { CUSTOM_FIELD_TYPES, MAX_SELECT_OPTIONS } from "./customfields.js";
import { DEPARTMENT_DEFS } from "./hr.js";
import {
  EVALUATION_CRITERIA,
  INTERVIEW_KINDS,
  INTERVIEW_KIND_MAP,
  INTERVIEW_STATUSES,
  RECOMMENDATIONS,
  SCORE_MAX,
  SCORE_MIN,
} from "./interviews.js";
import {
  APPLICATION_STATUSES,
  CANDIDATE_SOURCES,
  EDUCATION_LEVELS,
  EMPLOYMENT_TYPES,
  MAX_JOB_BULLETS,
  HIRING_CAMPAIGN_STATUSES,
  JOB_POST_STATUSES,
  HIRING_CHANNELS,
  MAX_PIPELINE_STAGES,
  STAGE_KEY_PATTERN,
  MAX_ANNOUNCEMENT,
  MAX_FORM_FIELDS,
} from "./recruit.js";
import { WORK_MODES } from "./orgsetup.js";

/** مخططات وحدة التوظيف — مشتركة بين الخادم والواجهة والرابط العلني. */

const Cuid = z.string().regex(/^[a-z0-9]{20,32}$/i, "معرّف غير صالح.");

const DEPARTMENT_KEYS_TUPLE = DEPARTMENT_DEFS.map((d) => d.key) as unknown as [
  string,
  ...string[],
];

const optionalText = (max = 500) =>
  z
    .string()
    .trim()
    .max(max, `النص أطول من ${max} حرفًا.`)
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional();

const dateOnly = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "التاريخ بصيغة سنة-شهر-يوم.");

const optionalDateOnly = dateOnly
  .nullable()
  .optional()
  .or(z.literal("").transform(() => null));

/* ==========================================================================
   حملات التعيين
   ========================================================================== */

export const CreateCampaignSchema = z
  .object({
    name: z.string().trim().min(2, "اسم الحملة قصير.").max(120),
    goal: z.coerce
      .number()
      .int("عدد التعيينات رقم صحيح.")
      .min(1, "الهدف واحدٌ على الأقل.")
      .max(999)
      .default(1),
    startsOn: dateOnly,
    endsOn: optionalDateOnly,
    notes: optionalText(2000),
    purpose: optionalText(2000),
    budget: z.coerce
      .number()
      .min(0, "الميزانية لا تكون سالبة.")
      .max(99_999_999.99)
      .nullable()
      .optional(),
    channels: z
      .array(z.enum(HIRING_CHANNELS))
      .max(HIRING_CHANNELS.length)
      .default([]),
    ownerId: Cuid.nullable().optional(),
  })
  .refine((v) => !v.endsOn || v.startsOn <= v.endsOn, {
    message: "تاريخ البداية بعد تاريخ النهاية.",
    path: ["endsOn"],
  })
  /* قناةٌ مكرّرةٌ تُضاعف روابطَها في الشاشة ولا تعني شيئًا في القياس. */
  .refine((v) => new Set(v.channels).size === v.channels.length, {
    message: "قناةٌ مكرّرة.",
    path: ["channels"],
  });
export type CreateCampaignInput = z.infer<typeof CreateCampaignSchema>;

/**
 * تعديل الحملة.
 *
 * يُكتب صريحًا ولا يُشتقّ بـ`partial`: المخطّط أعلاه عليه `refine`،
 * و`ZodEffects` لا يحمل `partial` ولا `omit` — مصيدةٌ مكتوبةٌ في ملفّ
 * التسليم يقع فيها كلُّ من نسيها.
 */
export const UpdateCampaignSchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  goal: z.coerce.number().int().min(1).max(999).optional(),
  purpose: optionalText(2000),
  budget: z.coerce
    .number()
    .min(0, "الميزانية لا تكون سالبة.")
    .max(99_999_999.99)
    .nullable()
    .optional(),
  channels: z
    .array(z.enum(HIRING_CHANNELS))
    .max(HIRING_CHANNELS.length)
    .refine((c) => new Set(c).size === c.length, { message: "قناةٌ مكرّرة." })
    .optional(),
  ownerId: Cuid.nullable().optional(),
  startsOn: dateOnly.optional(),
  endsOn: optionalDateOnly,
  status: z.enum(HIRING_CAMPAIGN_STATUSES).optional(),
  notes: optionalText(2000),
});
export type UpdateCampaignInput = z.infer<typeof UpdateCampaignSchema>;

/* ==========================================================================
   استمارة التقديم
   ========================================================================== */

export const FormFieldSchema = z.object({
  key: z
    .string()
    .trim()
    .regex(
      /^[a-z][a-z0-9_]{1,30}$/,
      "المفتاح حروفٌ إنجليزيةٌ وأرقامٌ وشرطة سفلية.",
    )
    .optional(),
  label: z.string().trim().min(1, "عنوان الحقل مطلوب.").max(120),
  type: z.enum(CUSTOM_FIELD_TYPES),
  options: z
    .array(z.string().trim().min(1).max(120))
    .max(MAX_SELECT_OPTIONS, `الخيارات أكثر من ${MAX_SELECT_OPTIONS}.`)
    .default([]),
  required: z.boolean().default(false),
  hint: optionalText(200),
  position: z.coerce.number().int().min(0).max(999).default(0),
  isActive: z.boolean().default(true),
});
export type FormFieldInput = z.infer<typeof FormFieldSchema>;

export const CreateFormSchema = z.object({
  name: z.string().trim().min(2, "اسم الاستمارة قصير.").max(120),
  intro: optionalText(2000),
  announcement: optionalText(MAX_ANNOUNCEMENT),
  isDefault: z.boolean().default(false),
  fields: z
    .array(FormFieldSchema)
    .max(MAX_FORM_FIELDS, `الحقول أكثر من ${MAX_FORM_FIELDS}.`)
    .default([]),
});
export type CreateFormInput = z.infer<typeof CreateFormSchema>;

export const UpdateFormSchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  intro: optionalText(2000),
  announcement: optionalText(MAX_ANNOUNCEMENT),
  isActive: z.boolean().optional(),
  isDefault: z.boolean().optional(),
  /**
   * الحقول تُرسل كاملةً لا مفردةً.
   *
   * محرّر الاستمارة يعيد الترتيب ويحذف ويضيف في جلسةٍ واحدة، وحفظُ ذلك
   * على ثلاثة طلبات يترك الاستمارة نصفَ محفوظةٍ إن انقطع الاتصال.
   */
  fields: z.array(FormFieldSchema).max(MAX_FORM_FIELDS).optional(),
});
export type UpdateFormInput = z.infer<typeof UpdateFormSchema>;

/* ==========================================================================
   إعلانات الوظائف
   ========================================================================== */

/**
 * قائمةُ بنودٍ في ملفّ الوظيفة.
 *
 * تُنظَّف عند الاستقبال لا عند العرض: بندٌ فارغٌ مرّ من نموذجٍ نصفِ مملوءٍ
 * يصير نقطةً خاليةً في الإعلان العلنيّ، ولا يُعرف بعدها أنُسي شيءٌ أم لا.
 */
const jobBullets = z
  .array(z.string().trim().max(400, "البند أطول من أربعمئة حرف."))
  .max(MAX_JOB_BULLETS, `البنود أكثر من ${MAX_JOB_BULLETS}.`)
  .transform((rows) => rows.map((r) => r.trim()).filter(Boolean))
  .default([]);

const money = z.coerce
  .number()
  .min(0, "المبلغ لا يكون سالبًا.")
  .max(99_999_999.99, "المبلغ أكبر من الحدّ المسموح.")
  .nullable()
  .optional();

export const CreateJobSchema = z
  .object({
    title: z.string().trim().min(2, "المسمّى الوظيفي قصير.").max(160),
    deptKey: z.enum(DEPARTMENT_KEYS_TUPLE).nullable().optional(),
    employmentType: z.enum(EMPLOYMENT_TYPES).default("FULL_TIME"),
    workMode: z.enum(WORK_MODES).default("ONSITE"),
    location: optionalText(160),
    openings: z.coerce
      .number()
      .int("عدد الشواغر رقم صحيح.")
      .min(1, "شاغرٌ واحدٌ على الأقل.")
      .max(999)
      .default(1),

    description: optionalText(8000),
    duties: jobBullets,
    techRequirements: jobBullets,
    softRequirements: jobBullets,
    benefits: jobBullets,
    languages: jobBullets,

    salaryMin: money,
    salaryMax: money,
    showSalary: z.boolean().default(false),

    experienceMin: z.coerce
      .number()
      .int("سنوات الخبرة رقم صحيح.")
      .min(0)
      .max(60)
      .nullable()
      .optional(),
    education: z.enum(EDUCATION_LEVELS).nullable().optional(),

    /* ما يُختار من إعداد المؤسسة ومن التطوير التنظيميّ. */
    positionId: Cuid.nullable().optional(),
    gradeId: Cuid.nullable().optional(),
    orgUnitId: Cuid.nullable().optional(),
    scheduleId: Cuid.nullable().optional(),
    costCenterId: Cuid.nullable().optional(),

    formId: Cuid.nullable().optional(),
    campaignId: Cuid.nullable().optional(),
    managerId: Cuid.nullable().optional(),
    closesOn: optionalDateOnly,
  })
  .strict()
  /*
   * أعلى النطاق ليس أقلَّ من أدناه.
   *
   * ونطاقٌ مقلوبٌ لا يُكتشف بالنظر في شاشةٍ فيها عشرون حقلًا — ولكنّه
   * يُكتشف في عرض العمل حين يُقترح راتبٌ خارج ما قُصد.
   */
  .refine(
    (v) =>
      v.salaryMin === null ||
      v.salaryMin === undefined ||
      v.salaryMax === null ||
      v.salaryMax === undefined ||
      v.salaryMax >= v.salaryMin,
    { message: "أعلى النطاق أقلّ من أدناه.", path: ["salaryMax"] },
  )
  /*
   * ولا يُعلَن نطاقٌ لم يُكتب.
   *
   * «يُعلَن الراتب» مع خانتين فارغتين تُنتج في الصفحة العلنيّة عنوانًا
   * بلا رقمٍ تحته — وهو أسوأُ من ألّا يُعلَن.
   */
  .refine(
    (v) =>
      !v.showSalary ||
      (v.salaryMin ?? null) !== null ||
      (v.salaryMax ?? null) !== null,
    {
      message: "لا يُعلَن نطاقٌ لم يُكتب — اكتب حدًّا واحدًا على الأقلّ.",
      path: ["showSalary"],
    },
  );
export type CreateJobInput = z.infer<typeof CreateJobSchema>;

/**
 * تعديلُ الوظيفة.
 *
 * يُكتب بيده ولا يُشتقّ بـ .partial(): المخطّطُ أعلاه صار ZodEffects
 * بسبب .refine()، و .partial() لا تعمل على ZodEffects — تُسقط الشروطَ
 * أو تنفجر. وهذا مزلقٌ وقع في هذا المستودع مرّةً من قبل.
 */
export const UpdateJobSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(2, "المسمّى الوظيفي قصير.")
      .max(160)
      .optional(),
    deptKey: z.enum(DEPARTMENT_KEYS_TUPLE).nullable().optional(),
    employmentType: z.enum(EMPLOYMENT_TYPES).optional(),
    workMode: z.enum(WORK_MODES).optional(),
    location: optionalText(160),
    openings: z.coerce.number().int().min(1).max(999).optional(),

    description: optionalText(8000),
    duties: jobBullets.optional(),
    techRequirements: jobBullets.optional(),
    softRequirements: jobBullets.optional(),
    benefits: jobBullets.optional(),
    languages: jobBullets.optional(),

    salaryMin: money,
    salaryMax: money,
    showSalary: z.boolean().optional(),

    experienceMin: z.coerce.number().int().min(0).max(60).nullable().optional(),
    education: z.enum(EDUCATION_LEVELS).nullable().optional(),

    positionId: Cuid.nullable().optional(),
    gradeId: Cuid.nullable().optional(),
    orgUnitId: Cuid.nullable().optional(),
    scheduleId: Cuid.nullable().optional(),
    costCenterId: Cuid.nullable().optional(),

    formId: Cuid.nullable().optional(),
    campaignId: Cuid.nullable().optional(),
    managerId: Cuid.nullable().optional(),
    closesOn: optionalDateOnly,
    status: z.enum(JOB_POST_STATUSES).optional(),
  })
  .strict()
  .refine(
    (v) =>
      v.salaryMin === null ||
      v.salaryMin === undefined ||
      v.salaryMax === null ||
      v.salaryMax === undefined ||
      v.salaryMax >= v.salaryMin,
    { message: "أعلى النطاق أقلّ من أدناه.", path: ["salaryMax"] },
  )
  /*
   * ولا يُعلَن نطاقٌ لم يُكتب — هنا كما في الإنشاء.
   *
   * كان الشرطُ في مخطّط الإنشاء وحدَه، فوظيفةٌ بلا نطاقٍ تُعلَّم «يُعلَن
   * الراتب» بتعديلٍ، فتُنتج في الصفحة العلنيّة عنوانًا بلا رقمٍ تحته.
   *
   * ولا يُفحص إلّا إذا أُرسل الحقلان معه: من عدّل «يُعلَن» وحدَه يُقرأ له
   * المحفوظُ في القاعدة، وهو ما يفحصه الحارسُ في الخدمة.
   */
  .refine(
    (v) =>
      !v.showSalary ||
      v.salaryMin === undefined ||
      v.salaryMax === undefined ||
      (v.salaryMin ?? null) !== null ||
      (v.salaryMax ?? null) !== null,
    {
      message: "لا يُعلَن نطاقٌ لم يُكتب — اكتب حدًّا واحدًا على الأقلّ.",
      path: ["showSalary"],
    },
  );
export type UpdateJobInput = z.infer<typeof UpdateJobSchema>;

export const JobsQuerySchema = z.object({
  status: z.enum(JOB_POST_STATUSES).optional(),
  campaignId: Cuid.optional(),
  deptKey: z.string().trim().max(40).optional(),
  q: z.string().trim().max(120).optional(),
});
export type JobsQuery = z.infer<typeof JobsQuerySchema>;

/* ==========================================================================
   المرشّحون والطلبات
   ========================================================================== */

/**
 * تقديمٌ من داخل النظام.
 *
 * موظّفُ الموارد البشرية يُدخل من وصله بالبريد أو حضر بنفسه، فالهاتف
 * مطلوبٌ هنا وليس اختياريًّا: هو هويّة المرشّح في قاعدة البيانات، ومن
 * أُدخل بلا هاتفٍ يُنشأ له ملفٌّ ثانٍ في المرّة القادمة.
 */
export const CreateApplicationSchema = z.object({
  jobPostId: Cuid,
  name: z.string().trim().min(2, "الاسم قصير.").max(160),
  phone: z
    .string()
    .trim()
    .regex(/^[\d\s+()-]{6,32}$/, "رقم الهاتف يحتوي على رموز غير مقبولة."),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email("صيغة البريد الإلكتروني غير صحيحة.")
    .nullable()
    .optional()
    .or(z.literal("").transform(() => null)),
  headline: optionalText(160),
  source: z.enum(CANDIDATE_SOURCES).default("OTHER"),
  notes: optionalText(2000),
  answers: z.record(z.unknown()).default({}),
});
export type CreateApplicationInput = z.infer<typeof CreateApplicationSchema>;

/** تقديمٌ من الرابط العلني — بلا مصدرٍ يختاره المتقدّم ولا ملاحظاتٍ داخلية. */
export const PublicApplySchema = z.object({
  name: z.string().trim().min(2, "اكتب اسمك كاملًا.").max(160),
  phone: z
    .string()
    .trim()
    .regex(/^[\d\s+()-]{6,32}$/, "رقم الهاتف غير صحيح."),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .email("صيغة البريد الإلكتروني غير صحيحة.")
    .nullable()
    .optional()
    .or(z.literal("").transform(() => null)),
  headline: optionalText(160),
  answers: z.record(z.unknown()).default({}),
  /**
   * القناةُ التي جاء منها — من عنوان الرابط لا من سؤالٍ في الاستمارة.
   *
   * ويُقبل غيابُها ويُقبل ما لا يُعرف منها بلا ردّ: العنوانُ يُنسخ ويُلصق
   * وتُقَصّ منه أجزاءٌ، وطلبُ تقديمٍ يُردّ لأنّ معامل قناةٍ تشوّه في
   * النسخ خسارةُ مرشّحٍ مقابل لا شيء.
   */
  channel: z.enum(HIRING_CHANNELS).nullable().optional().catch(null),
});
export type PublicApplyInput = z.infer<typeof PublicApplySchema>;

/**
 * نقلُ طلبٍ إلى عمود.
 *
 * المفتاحُ نصٌّ لا قائمةٌ مغلقة: الأعمدةُ صارت بياناتِ الشركة، ومخطّطٌ
 * يعدّ سبعةَ مفاتيحَ كان يمنع كلَّ عمودٍ تضيفه. ووجودُ المفتاح يُتحقّق في
 * الخادم من أعمدة هذه الشركة وحدَها.
 */
export const MoveApplicationSchema = z.object({
  stage: z.string().trim().regex(STAGE_KEY_PATTERN, "مفتاح مرحلة غير صالح."),
  note: optionalText(1000),
});
export type MoveApplicationInput = z.infer<typeof MoveApplicationSchema>;

/**
 * قرارٌ على الطلب.
 *
 * الاستبعادُ يطلب سببًا ولا يقبل صمتًا: من فُرز بلا سبب لا يُعرف بعد شهر
 * لماذا فُرز، ولا يُقاس أيُّ الأسباب يتكرّر — وذاك ما يُصلح الإعلان نفسه.
 */
export const DecideApplicationSchema = z
  .object({
    status: z.enum(APPLICATION_STATUSES),
    reason: optionalText(1000),
  })
  .refine((v) => v.status !== "REJECTED" || !!v.reason, {
    message: "الاستبعاد يحتاج سببًا مكتوبًا.",
    path: ["reason"],
  });
export type DecideApplicationInput = z.infer<typeof DecideApplicationSchema>;

export const ApplicationsQuerySchema = z.object({
  jobPostId: Cuid.optional(),
  campaignId: Cuid.optional(),
  stage: z.string().trim().regex(STAGE_KEY_PATTERN).optional(),
  status: z.enum(APPLICATION_STATUSES).optional(),
  q: z.string().trim().max(120).optional(),
});
export type ApplicationsQuery = z.infer<typeof ApplicationsQuerySchema>;

export const AddNoteSchema = z.object({
  note: z.string().trim().min(1, "الملاحظة فارغة.").max(2000),
});
export type AddNoteInput = z.infer<typeof AddNoteSchema>;

/* ==========================================================================
   عرض العمل
   ========================================================================== */

export const CreateOfferSchema = z.object({
  title: z.string().trim().min(2, "المسمّى الوظيفي قصير.").max(160),
  deptKey: z.enum(DEPARTMENT_KEYS_TUPLE).nullable().optional(),
  employmentType: z.enum(EMPLOYMENT_TYPES).default("FULL_TIME"),
  startDate: dateOnly,
  baseSalary: z.coerce
    .number()
    .min(1, "الراتب مطلوب.")
    .max(99_999_999.99, "الراتب أكبر من الحدّ المسموح."),
  probationMonths: z.coerce
    .number()
    .int()
    .min(0)
    .max(12, "فترة الاختبار أطول من سنة.")
    .default(3),
  benefits: optionalText(2000),
  /** يُترك فارغًا فيُولَّد الخطاب من الحقول. */
  body: optionalText(20_000),
  expiresOn: optionalDateOnly,
});
export type CreateOfferInput = z.infer<typeof CreateOfferSchema>;

/**
 * تعديلُ العرض قبل إرساله.
 *
 * والنصُّ يُحرَّر ولا يُعاد توليدُه: عرضٌ أُعيد توليدُه بعد مراجعته يمحو
 * ما كتبه من راجعه.
 */
export const UpdateOfferSchema = CreateOfferSchema.partial().strict();
export type UpdateOfferInput = z.infer<typeof UpdateOfferSchema>;

/** ردُّ المرشّح على الرابط العلنيّ. */
export const AnswerOfferSchema = z
  .object({
    answer: z.enum(["ACCEPT", "DECLINE"]),
    /**
     * الاسمُ كما يكتبه هو.
     *
     * يُطلب عند القبول ولا يُفترض: القبولُ التزامٌ، وما يُثبته النظام
     * يُسأل عنه بعد شهور — واسمٌ منسوخٌ من الملفّ ليس توقيعًا.
     */
    name: z.string().trim().max(160).optional(),
    reason: optionalText(1000),
  })
  .refine((v) => v.answer !== "ACCEPT" || (v.name?.length ?? 0) >= 2, {
    message: "اكتب اسمك كاملًا لتأكيد القبول.",
    path: ["name"],
  });
export type AnswerOfferInput = z.infer<typeof AnswerOfferSchema>;

/**
 * فتحُ الملفّ الوظيفيّ بعد القبول.
 *
 * ما لا يعرفه العرضُ يُسأل هنا: الرقمُ القوميّ والمدير المباشر ورصيد
 * الإجازات. ولا يُخمَّن شيءٌ منها — ملفٌّ وظيفيٌّ بأرقامٍ مخمَّنة يُبنى
 * عليه مرتّبٌ وتأمين.
 */
export const HireSchema = z.object({
  code: optionalText(40),
  nationalId: optionalText(30),
  managerId: Cuid.nullable().optional(),
  annualLeaveBalance: z.coerce.number().min(0).max(365).default(21),
  hireDate: dateOnly.optional(),
});
export type HireInput = z.infer<typeof HireSchema>;

/* ==========================================================================
   المقابلات
   ========================================================================== */

/**
 * حجزُ موعدِ لقاء.
 *
 * الرابطُ يُلصق ولا يُولَّد: لا تكاملَ مع زووم ولا مِيت في النظام، وزرٌّ
 * يوهم بإنشاء غرفةٍ ثمّ لا يُنشئها أسوأُ من خانةٍ تُملأ باليد.
 */
export const ScheduleInterviewSchema = z
  .object({
    kind: z.enum(INTERVIEW_KINDS),
    scheduledAt: z.coerce.date(),
    minutes: z.coerce
      .number()
      .int()
      .min(5, "المدّة خمس دقائق على الأقل.")
      .max(480, "المدّة أطول من ثماني ساعات.")
      .default(45),
    interviewerId: Cuid.nullable().optional(),
    place: optionalText(300),
    meetingUrl: optionalText(500),
    note: optionalText(1000),
  })
  .refine(
    (v) => INTERVIEW_KIND_MAP[v.kind].needsLink === false || !!v.meetingUrl,
    { message: "الاجتماع أونلاين يحتاج رابطًا يُلصق.", path: ["meetingUrl"] },
  )
  .refine((v) => INTERVIEW_KIND_MAP[v.kind].needsPlace === false || !!v.place, {
    message: "المقابلة الحضورية تحتاج عنوانًا يُكتب للمرشّح.",
    path: ["place"],
  });
export type ScheduleInterviewInput = z.infer<typeof ScheduleInterviewSchema>;

/**
 * تعديلُ الموعد أو إقفالُه.
 *
 * يُكتب صريحًا لا بـ`partial`: المخطّط أعلاه عليه `refine`، و`ZodEffects`
 * لا يحمل `partial` ولا `omit`.
 */
export const UpdateInterviewSchema = z.object({
  scheduledAt: z.coerce.date().optional(),
  minutes: z.coerce.number().int().min(5).max(480).optional(),
  interviewerId: Cuid.nullable().optional(),
  place: optionalText(300),
  meetingUrl: optionalText(500),
  status: z.enum(INTERVIEW_STATUSES).optional(),
  outcome: optionalText(2000),
});
export type UpdateInterviewInput = z.infer<typeof UpdateInterviewSchema>;

export const InterviewsQuerySchema = z.object({
  from: z.coerce.date().optional(),
  to: z.coerce.date().optional(),
  status: z.enum(INTERVIEW_STATUSES).optional(),
  jobPostId: Cuid.optional(),
});
export type InterviewsQuery = z.infer<typeof InterviewsQuerySchema>;

/* ==========================================================================
   التقييم
   ========================================================================== */

/**
 * حكمُ مقيّمٍ على مرشّح.
 *
 * التوصيةُ مطلوبةٌ والدرجاتُ اختيارية: من قابل مرشّحًا في مكالمةٍ قصيرة
 * لا يحكم على انسجامه مع الفريق، وإجبارُه على درجةٍ في كلّ معيارٍ يُنتج
 * أوساطًا مكتوبةً لتملأ الخانة لا لتقول شيئًا.
 *
 * و«لا أرشّحه» يطلب سببًا: رفضٌ بلا سببٍ لا يُراجَع ولا يُقاس.
 */
export const SubmitEvaluationSchema = z
  .object({
    interviewId: Cuid.nullable().optional(),
    scores: z
      .record(z.coerce.number().int().min(SCORE_MIN).max(SCORE_MAX))
      .default({}),
    recommendation: z.enum(RECOMMENDATIONS),
    notes: optionalText(4000),
  })
  .refine((v) => v.recommendation !== "NO" || !!v.notes, {
    message: "عدم الترشيح يحتاج سببًا مكتوبًا.",
    path: ["notes"],
  })
  .refine(
    (v) =>
      Object.keys(v.scores).every((k) =>
        EVALUATION_CRITERIA.includes(k as never),
      ),
    { message: "معيارٌ غير معروف.", path: ["scores"] },
  );
export type SubmitEvaluationInput = z.infer<typeof SubmitEvaluationSchema>;

/* ==========================================================================
   أعمدة المسار
   ========================================================================== */

/**
 * عمودٌ يُضاف أو يُعدَّل.
 *
 * المفتاحُ يُرسل عند الإنشاء وحدَه ويُهمَل عند التعديل: عمودُ المرحلة في
 * الطلب يحمله نصًّا، وتغييرُه يترك طلباتٍ في عمودٍ لا وجودَ له. ولو قُبل
 * تغييرُه لاحتاج ترحيلَ صفوفٍ في كلّ حفظة.
 */
export const UpsertStageSchema = z.object({
  key: z
    .string()
    .trim()
    .regex(
      STAGE_KEY_PATTERN,
      "المفتاح حروفٌ لاتينيّةٌ كبيرةٌ وأرقامٌ وشرطةٌ سفليّة.",
    )
    .optional(),
  label: z.string().trim().min(2, "اسم المرحلة قصير.").max(60),
  tone: z
    .enum(["neutral", "info", "brand", "success", "warning", "danger"])
    .default("neutral"),
  action: optionalText(160),
  description: optionalText(400),
  order: z.coerce.number().int().min(0).max(99).default(0),
  isActive: z.boolean().default(true),
});
export type UpsertStageInput = z.infer<typeof UpsertStageSchema>;

/**
 * إعادةُ ترتيب الأعمدة.
 *
 * القائمةُ كاملةً لا حركةٌ واحدة: السحبُ يعيد ترتيبَ ثلاثةٍ في نقلةٍ
 * واحدة، وحفظُ ذلك على ثلاثة نداءاتٍ يترك المسارَ نصفَ مرتَّبٍ إن انقطع
 * الاتصال.
 */
export const ReorderStagesSchema = z.object({
  keys: z
    .array(z.string().trim().regex(STAGE_KEY_PATTERN))
    .min(1)
    .max(MAX_PIPELINE_STAGES)
    .refine((k) => new Set(k).size === k.length, {
      message: "مفتاحٌ مكرّرٌ في الترتيب.",
    }),
});
export type ReorderStagesInput = z.infer<typeof ReorderStagesSchema>;
